import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo } from 'react';
import { Button, Typography, Col, Row, Space, Form, Spin, message } from 'antd';
import { useRequest } from 'ahooks';
import { UserInfoContext } from '../../components/header/index'
import { Profile } from './components/Profile';
import { Qualification } from './components/Qualification';
import { SuccessResult } from './components/SuccessResult';
import moment from 'moment';
import get from 'lodash/get';
import styles from './index.module.less';
import { submitApplyInfo, queryAuditInfoByCompanyId, getCompanyInfo, EcCompanyInfo } from './service';

import { AUDIT_STATUS } from './dataType';

const { Text } = Typography;
const formLayout = {
  labelCol: {
    span: 6,
  },
  wrapperCol: {
    span: 12,
  },
}

const uploadFields = [
  'license',
  'creditProve',
  'qualitySystem',
  'supplyContract',
  'invoice',
  'rentContract',
  'electricalEngineerContract',
  'structuralEngineerContract',
  'softwareEngineerContract',
  'regulatoryRegimeFiles',
  'operateProcess',
  'testStandard',
  'initialData',
  'equipmentPhoto',
  'equipmentInspectionCertificate',
  'opsManual',
  'standingBook',
];

export const DirectApply: FunctionComponent = () => {
  const userInfo = useContext(UserInfoContext);
  if (userInfo && userInfo.loginError) {
    location.href = window.Login();
  }
  const [form] = Form.useForm();
  const queryAuditLogs = useRequest(queryAuditInfoByCompanyId, {
    manual: true,
    formatResult(res) {
      const detailStr = get(res, [0, 'auditDetail'], "{}");
      try {
        return JSON.parse(detailStr);
      } catch (err) {
        return {};
      }
    }
  });
  // console.log(queryAuditLogs.data);
  // useEffect(() => {
  //   queryAuditLogs.run(330621199302221500);
  // }, [])
  const companyInfoRequest = useRequest(getCompanyInfo, {
    initialData: {
      auditState: AUDIT_STATUS.INIT,
    },
    manual: true,
    formatResult(res) {
      return res.length > 0 ? res[0] : {
        auditState: AUDIT_STATUS.INIT,
      };
    },
    onSuccess(data) {
      const values = {
        ...data,
      };
      if (values['registerTime']) {
        values['registerTime'] = moment(values['registerTime']);
      }
      if (values['firstAuditTime']) {
        values['firstAuditTime'] = moment(values['firstAuditTime']);
      }
      if (values['lastAuditTime']) {
        values['lastAuditTime'] = moment(values['lastAuditTime']);
      }
      Object.keys(values).forEach(key => {
        if (uploadFields.includes(key)) {
          if (values[key]) {
            Object.assign(values, {
              [key]: [{
                url: values[key],
                name: values[key],
              }],
            });
          } else {
            Object.assign(values, {
              [key]: [],
            });
          }
        }
      });
      // console.log(values);
      if (values.companyId) {
        queryAuditLogs.run(values.companyId);
      }
      form.setFieldsValue(values);
    }
  });
  const canEdit = useMemo(() => {
    return companyInfoRequest.data?.auditState === AUDIT_STATUS.INIT;
  }, [companyInfoRequest.data]);
  useEffect(() => {
    if (userInfo?.realPhone) {
      companyInfoRequest.run({
        linkPhoneNum: userInfo.realPhone,
      });
      form.setFieldsValue({
        linkPhoneNum: userInfo.realPhone,
      })
    }
  }, [userInfo?.realPhone, form]);
  const submitRequest = useRequest(submitApplyInfo, {
    manual: true,
    onSuccess() {
      message.success('申请成功', 3, () => {
        location.reload();
      });
    },
    onError(res) {
      message.error(res.msg);
    }
  });
  const handleEdit = useCallback(() => {
    companyInfoRequest.mutate({
      ...companyInfoRequest.data,
      auditState: AUDIT_STATUS.INIT,
    });
  }, [companyInfoRequest.mutate, companyInfoRequest.data]);
  const handleSubmit = useCallback(() => {
    form.validateFields().then(values => {
      const submitValues = {
        auditState: AUDIT_STATUS.AUDIT,
      };
      if (companyInfoRequest.data['id']) {
        Object.assign(submitValues, {
          id: companyInfoRequest.data['id'],
        });
      }
      Object.keys(values).forEach(key => {
        if (Array.isArray(values[key])) {
          Object.assign(submitValues, {
            [key]: values[key][0].response?.url || values[key][0].url,
          });
        } else if (key === 'registerTime') {
          Object.assign(submitValues, {
            [key]: values[key].valueOf(),
          });
        } else if (key === 'linkPhoneNum') {
          if (/^\d+$/.test(values[key])) {
            Object.assign(submitValues, {
              [key]: values[key],
            });
          }
        } else {
          Object.assign(submitValues, {
            [key]: values[key],
          });
        }
      });
      submitRequest.run(submitValues as EcCompanyInfo);
    }).catch(err => {
      console.log(err);
    });
  }, [submitRequest.run, form.validateFields, companyInfoRequest.data]);
  return (
    <Spin spinning={companyInfoRequest.loading}>
      <div className={styles.DirectApply__Wrapper}>
        <SuccessResult status={companyInfoRequest.data?.auditState}>
          <h1 className={styles.DirectApply__Title}>成为开发者</h1>
          <div className={styles.DirectApply__Actions}>
            { companyInfoRequest.data?.auditState === AUDIT_STATUS.AUDIT && (
              <Space direction="vertical" align="end">
                <Button disabled>审核中</Button>
              </Space>
            )}
            { companyInfoRequest.data?.auditState === AUDIT_STATUS.REJECT && (
              <Space direction="vertical" align="end">
                <Space>
                  <Button>审核不通过</Button>
                  <Button type="primary" onClick={handleEdit}>修改信息</Button>
                </Space>
                <Text>原因：{companyInfoRequest.data.auditInfo}</Text>
              </Space>
            )}
          </div>
          <Form
            form={form}
            initialValues={companyInfoRequest.data}
            size={'middle'}
            className={styles.DirectApply__Form}
            {...formLayout}
          >
            <Profile editable={canEdit} className={styles.DirectApply__Block} />
            <Qualification
              form={form}
              editable={canEdit}
              className={styles.DirectApply__Block} auditResult={queryAuditLogs.data}
              showAuditResult={companyInfoRequest.data?.auditState === AUDIT_STATUS.REJECT}
            />
          </Form>
          <Row justify="center" style={{ width: '100%' }}>
            <Col>
              <Button
                type="primary"
                loading={submitRequest.loading}
                onClick={handleSubmit}
                disabled={!canEdit}
              >提交申请</Button>
            </Col>
          </Row>
        </SuccessResult>
      </div>
    </Spin>
  );
}